import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import packages from "../../../apiData/yogaPackage.json";
import usePackagesAnalytics from "./usePackagesAnalytics";
import { PackagesSeoMeta } from "./PackagesSeoMeta";
import {
  ShieldIcon,
  CalendarCheckIcon,
  HeartPulseIcon,
  TrophyIcon,
  SeedIcon,
  SproutHeartIcon,
  LotusIcon,
  FlameIcon,
  ScrollIcon,
  FlaskIcon,
  PersonUpIcon,
  ChartUpIcon,
  HeadsetIcon,
  ArrowRightIcon,
  CheckIcon,
  StarIcon,
} from "./Packages.icons";

import {
  Section,
  BgBlob,
  DotGrid,
  Inner,
  HeroContent,
  HeroEyebrow,
  HeroTitle,
  HeroDesc,
  TrustBadgeRow,
  TrustBadge,
  TrustBadgeIconWrap,
  TrustBadgeTitle,
  TrustBadgeSub,
  HeaderWrap,
  Eyebrow,
  SectionTitle,
  SectionDesc,
  Divider,
  ProgramTabRow,
  ProgramTab,
  PackageBlock,
  PackageLayout,
  SidePanel,
  SidePanelImg,
  SidePanelOverlay,
  SidePanelMeta,
  PackageEyebrow,
  PackageName,
  PackageDivider,
  CardsArea,
  CardsGrid,
  Card,
  PlanChip,
  PopularBadge,
  PriceBubble,
  PriceBubbleInner,
  PriceLabel,
  PriceValue,
  CardTitle,
  FeatureList,
  FeatureItem,
  JoinBtn,
  ConsultBar,
  ConsultInfo,
  ConsultIconWrap,
  ConsultText,
  ConsultBtn,
  WhySection,
  WhyGrid,
  WhyCard,
  WhyIconWrap,
  WhyTitle,
  WhyDesc,
  CtaBanner,
  BannerOrb,
  BannerTitle,
  BannerDesc,
  BannerBtn,
} from "./Packages.styles";

// ─── Rebrand cleanup ──────────────────────────────────────────────────────────
// yogaPackage.json's `name` fields are literally "Pre-Conception Package’s",
// "Prenatal Package’s", etc. — grammatically off (stray possessive) and, now
// that this page is "Our Program" rather than "Packages," redundant wording
// too. This strips the trailing "Package['’]s?" for anything shown on the
// page/in its metadata, without touching the source JSON (so anything else
// reading yogaPackage.json elsewhere in the app is unaffected).
const formatProgramName = (name = "") => name.replace(/\s*Package(?:['’]s|s)?\s*$/i, "").trim();

const DISPLAY_PACKAGES = packages.map((pkg) => ({
  ...pkg,
  name: formatProgramName(pkg.name),
}));

// One icon per program tab, in the same order as yogaPackage.json. If a
// program is ever added beyond this list, PROGRAM_TAB_ICONS[i] is undefined
// and the tab falls back to LotusIcon below rather than rendering blank.
const PROGRAM_TAB_ICONS = [SeedIcon, SproutHeartIcon, LotusIcon, FlameIcon];

// ─── Hero trust badges ────────────────────────────────────────────────────────
const TRUST_BADGES = [
  { icon: <ShieldIcon />, title: "Expert Designed", subtitle: "By Yoga Therapists" },
  { icon: <CalendarCheckIcon />, title: "Structured & Safe", subtitle: "Progressive Programs" },
  { icon: <HeartPulseIcon />, title: "Holistic Approach", subtitle: "Mind, Body, Breath" },
  { icon: <TrophyIcon />, title: "Proven Results", subtitle: "Better Health & Peace" },
];

// ─── "Why our programs work" grid ─────────────────────────────────────────────
const WHY_ITEMS = [
  {
    icon: <ScrollIcon />,
    title: "Ancient Wisdom",
    desc: "Rooted in authentic yogic traditions and scriptures.",
  },
  {
    icon: <FlaskIcon />,
    title: "Modern Science",
    desc: "Programs designed with modern research & expertise.",
  },
  {
    icon: <PersonUpIcon />,
    title: "Personal Guidance",
    desc: "One-on-one support tailored to your unique journey.",
  },
  {
    icon: <ChartUpIcon />,
    title: "Real Results",
    desc: "Long-lasting benefits for a healthier, happier you.",
  },
];

// ─── PriceCard ────────────────────────────────────────────────────────────────

const PriceCard = ({ classItem, index, totalCards, onJoin }) => {
  const isPopular  = index === Math.floor(totalCards / 2);
  const features   = classItem.classProperty
    .split("--")
    .map((f) => f.trim())
    .filter(Boolean);

  return (
    <Card
      $popular={isPopular}
      onClick={onJoin}
      role="button"
      tabIndex={0}
      aria-label={`${classItem.className} plan — ${classItem.classPrice} — click to view details`}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onJoin(); }}
    >
      {isPopular && (
        <PopularBadge>
          <StarIcon /> Most Popular
        </PopularBadge>
      )}

      <PlanChip $popular={isPopular}>Plan {index + 1}</PlanChip>

      <PriceBubble>
        <PriceBubbleInner $popular={isPopular}>
          <PriceLabel $popular={isPopular}>Price</PriceLabel>
          <PriceValue $popular={isPopular}>{classItem.classPrice}</PriceValue>
        </PriceBubbleInner>
      </PriceBubble>

      <CardTitle $popular={isPopular}>{classItem.className}</CardTitle>

      <FeatureList>
        {features.map((feature, i) => (
          <FeatureItem key={i} $popular={isPopular}>
            <CheckIcon />
            {feature}
          </FeatureItem>
        ))}
      </FeatureList>

      <JoinBtn
        type="button"
        $popular={isPopular}
        onClick={(e) => { e.stopPropagation(); onJoin(); }}
        aria-label={`View details for ${classItem.className}`}
      >
        Enroll Now <ArrowRightIcon />
      </JoinBtn>
    </Card>
  );
};

// ─── Packages (rendered as "Our Program") ────────────────────────────────────

const Packages = () => {
  const navigate   = useNavigate();
  const analytics  = usePackagesAnalytics();
  const [activeIndex, setActiveIndex] = useState(0);

  // ── Page view on mount ──
  useEffect(() => {
    analytics.trackPageView("Our Program — Yoga Program & Pricing", window.location.href);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick         = useCallback(() => navigate("/contactUs"),    [navigate]);
  const handleClickPrenatal = useCallback(() => navigate("/prenatalQuery"), [navigate]);

  const activePackage = useMemo(
    () => DISPLAY_PACKAGES[activeIndex] ?? DISPLAY_PACKAGES[0],
    [activeIndex]
  );
  const isPrenatal   = activePackage.id < 4;
  const baseNavigate = isPrenatal ? handleClickPrenatal : handleClick;

  return (
    <Section id="packages">

      {/* ── SEO — PackagesSeoMeta mirrors BlogListMeta pattern ── */}
      <PackagesSeoMeta packages={DISPLAY_PACKAGES} />

      <BgBlob className="b1" aria-hidden="true" />
      <BgBlob className="b2" aria-hidden="true" />
      <BgBlob className="b3" aria-hidden="true" />
      <DotGrid aria-hidden="true" />

      {/* ── Hero: eyebrow + title + trust badges ── */}
      <HeroContent>
        <HeroEyebrow>✦ Our Program</HeroEyebrow>
        <HeroTitle>Transform Your Life with the Right Yoga Program</HeroTitle>
        <HeroDesc>
          Scientifically designed yoga programs that blend ancient wisdom with
          modern wellbeing. Choose a path that fits your goals, lifestyle and
          health needs.
        </HeroDesc>

        <TrustBadgeRow>
          {TRUST_BADGES.map((badge) => (
            <TrustBadge key={badge.title}>
              <TrustBadgeIconWrap>{badge.icon}</TrustBadgeIconWrap>
              <span>
                <TrustBadgeTitle>{badge.title}</TrustBadgeTitle>
                <TrustBadgeSub>{badge.subtitle}</TrustBadgeSub>
              </span>
            </TrustBadge>
          ))}
        </TrustBadgeRow>
      </HeroContent>

      <Inner>
        <HeaderWrap>
          <Eyebrow>✦ Choose Your Path</Eyebrow>
          <SectionTitle>Programs for Every Goal</SectionTitle>
          <Divider aria-hidden="true" />
          <SectionDesc>
            Every program is carefully designed for a specific purpose,
            blending foundational yoga with exclusive practices selected to
            help you reach your goals.
          </SectionDesc>
        </HeaderWrap>

        {/* ── Program tabs — real programs from yogaPackage.json ── */}
        <ProgramTabRow role="tablist" aria-label="Choose a yoga program">
          {DISPLAY_PACKAGES.map((pkg, i) => {
            const TabIcon = PROGRAM_TAB_ICONS[i] || LotusIcon;
            return (
              <ProgramTab
                key={pkg.id}
                type="button"
                role="tab"
                aria-selected={i === activeIndex}
                $active={i === activeIndex}
                onClick={() => setActiveIndex(i)}
              >
                <TabIcon />
                {pkg.name}
              </ProgramTab>
            );
          })}
        </ProgramTabRow>

        {/* ── Active program: image panel + pricing cards ── */}
        <PackageBlock key={activePackage.id}>
          <PackageLayout>
            <SidePanel>
              <SidePanelImg
                src={activePackage.image}
                alt={activePackage.name}
                loading="lazy"
              />
              <SidePanelOverlay aria-hidden="true" />
              <SidePanelMeta>
                <PackageEyebrow>✦ Program {activeIndex + 1}</PackageEyebrow>
                <PackageName>
                  {activePackage.name.replace(/^(.*)\s/, "$1\n")}
                </PackageName>
                <PackageDivider aria-hidden="true" />
              </SidePanelMeta>
            </SidePanel>

            <CardsArea>
              <CardsGrid $count={activePackage.classArray.length}>
                {activePackage.classArray.map((classItem, cardIndex) => {
                  const isPopular = cardIndex === Math.floor(activePackage.classArray.length / 2);

                  const onJoin = () => {
                    analytics.trackJoinClick(
                      activePackage,
                      classItem,
                      cardIndex,
                      isPrenatal,
                      isPopular,
                    );
                    baseNavigate();
                  };

                  return (
                    <PriceCard
                      key={classItem.id}
                      classItem={classItem}
                      index={cardIndex}
                      totalCards={activePackage.classArray.length}
                      onJoin={onJoin}
                    />
                  );
                })}
              </CardsGrid>
            </CardsArea>
          </PackageLayout>
        </PackageBlock>

        {/* ── Consultation nudge ── */}
        <ConsultBar>
          <ConsultInfo>
            <ConsultIconWrap>
              <HeadsetIcon />
            </ConsultIconWrap>
            <ConsultText>
              <strong>Not sure which program is right for you?</strong>
              <span>Talk to our yoga experts and find the best path for your goals.</span>
            </ConsultText>
          </ConsultInfo>
          <ConsultBtn
            type="button"
            onClick={() => {
              analytics.trackCtaBannerClick();
              handleClick();
            }}
            aria-label="Book a free consultation"
          >
            Book a Free Consultation <ArrowRightIcon />
          </ConsultBtn>
        </ConsultBar>

        {/* ── Why our programs work ── */}
        <WhySection>
          <HeaderWrap>
            <Eyebrow>✦ Why Our Programs Work</Eyebrow>
            <SectionTitle>A Holistic Approach to Wellbeing</SectionTitle>
            <Divider aria-hidden="true" />
          </HeaderWrap>

          <WhyGrid>
            {WHY_ITEMS.map((item, i) => (
              <WhyCard key={item.title} $index={i}>
                <WhyIconWrap>{item.icon}</WhyIconWrap>
                <WhyTitle>{item.title}</WhyTitle>
                <WhyDesc>{item.desc}</WhyDesc>
              </WhyCard>
            ))}
          </WhyGrid>
        </WhySection>

        <CtaBanner>
          <BannerOrb className="o1" aria-hidden="true" />
          <BannerOrb className="o2" aria-hidden="true" />
          <BannerTitle>Your Journey to a Better You Starts Here</BannerTitle>
          <BannerDesc>
            Consistent practice today, extraordinary results tomorrow. Pick a
            program, book your first class, and take the first step toward
            balance, strength, and inner peace.
          </BannerDesc>
          <BannerBtn
            type="button"
            onClick={() => {
              analytics.trackCtaBannerClick();
              handleClick();
            }}
            aria-label="Start your yoga journey"
          >
            Start Your Journey <ArrowRightIcon />
          </BannerBtn>
        </CtaBanner>
      </Inner>
    </Section>
  );
};

Packages.displayName = "Packages";
export default React.memo(Packages);